// Lazy loaded modules - will be loaded only when initialize() is called
let StatsigClient = null
let runStatsigSessionReplay = null
let Statsig = null
let sessionReplayInitialized = false
let isInitializing = false
let initializationPromise = null

async function loadStatsigModules() {
  if (!StatsigClient) {
    const statsigModule = await import('@statsig/js-client')
    StatsigClient = statsigModule.StatsigClient
  }
  return StatsigClient
}

async function loadSessionReplayModule() {
  if (!runStatsigSessionReplay) {
    const sessionReplayModule = await import('@statsig/session-replay')
    runStatsigSessionReplay = sessionReplayModule.runStatsigSessionReplay
  }
  return runStatsigSessionReplay
}

async function initializeSessionReplay(client) {
  if (sessionReplayInitialized) {
    return
  }
  try {
    const sessionReplay = await loadSessionReplayModule()
    sessionReplay(client)
    sessionReplayInitialized = true
  } catch (error) {
    console.error('Failed to initialize Session Replay:', error)
  }
}

export function logSessionReplayGateStatus(metaData = null) {
  // Skip if not initialized yet - don't wait
  if (!Statsig || isInitializing || !metaData) return

  try {
    updateUser({
      customIDs: {
        order_id: metaData.order_id,
        merchant_id: metaData.merchant_id,
        checkout_id: metaData.checkout_id,
      },
    }).then(() => {
      const canRecord = Statsig.checkGate(
        'gate_perm__checkout_session_recording'
      )
      if (canRecord) {
        Statsig.logEvent('session_recording_started', null, metaData)
        console.warn('Statsig session recording gate is enabled')
      }
    })
  } catch (error) {
    console.warn('logSessionReplayGateStatus error:', error)
  }
}

function isStatsigReady() {
  // Statsig can be used immediately after instantiation with cached/default values
  // It will update when initialization completes
  return Statsig !== null
}

export function getABTProvider() {
  return {
    isFeatureEnabled(featureName) {
      if (!isStatsigReady()) {
        return false
      }
      try {
        return Statsig.checkGate(featureName) || false
      } catch (error) {
        console.warn('Statsig checkGate error:', error)
        return false
      }
    },

    getExperiment(experimentName) {
      if (!isStatsigReady()) {
        return {}
      }
      try {
        const experiment = Statsig.getExperiment(experimentName)
        return experiment?.value || {}
      } catch (error) {
        console.warn('Statsig getExperiment error:', error)
        return {}
      }
    },

    getConfigurations(configureName) {
      if (!isStatsigReady()) {
        return {}
      }
      try {
        const config = Statsig.getDynamicConfig(configureName)
        return config?.value || {}
      } catch (error) {
        console.warn('Statsig getDynamicConfig error:', error)
        return {}
      }
    },

    async updateUser(user) {
      if (!isStatsigReady()) {
        console.warn('Statsig is not ready. Waiting for initialization...')
        // Wait for initialization if it's in progress
        if (initializationPromise) {
          await initializationPromise
        }
        if (!isStatsigReady()) {
          console.error('Statsig initialization failed or timed out')
          return
        }
      }

      try {
        // Note: With the new Session Replay API, recording is managed automatically
        // The session replay will continue recording during user updates
        await Statsig.updateUserAsync(user, { timeoutMs: 3000 })
      } catch (error) {
        console.error('Statsig updateUser error:', error)
      }
    },

    logEvent(eventName, eventValue = null, metaData) {
      if (!isStatsigReady()) {
        // Queue events if Statsig is not ready yet (optional: implement event queueing)
        return
      }
      try {
        Statsig.logEvent(eventName, eventValue, metaData)
      } catch (error) {
        console.warn('Statsig logEvent error:', error)
      }
    },

    shutdown() {
      if (Statsig) {
        try {
          Statsig.shutdown()
        } catch (error) {
          console.error('Statsig shutdown error:', error)
        }
      }
    },
  }
}

export async function initialize(sdkKey, user, options) {
  // Prevent multiple initializations
  if (Statsig || isInitializing) {
    return initializationPromise || Promise.resolve()
  }

  isInitializing = true

  // Lazy load Statsig modules
  initializationPromise = (async () => {
    try {
      const StatsigClientClass = await loadStatsigModules()

      // Initialize Statsig client (non-blocking)
      Statsig = new StatsigClientClass(sdkKey, user, options)
      window.statsigClient = Statsig

      // Initialize Session Replay BEFORE initializeAsync (required by Statsig)
      await initializeSessionReplay(Statsig)

      // Start initialization in the background without blocking
      await Statsig.initializeAsync({ timeoutMs: 3000 })

      isInitializing = false
      // Log event if gate is enabled (for tracking purposes)
    } catch (error) {
      isInitializing = false
      console.error('Statsig initialization failed:', error)
      // Statsig will still work with cached/default values
      throw error
    }
  })()

  // Don't await - let it initialize in the background
  return initializationPromise
}

export async function updateUser(user) {
  if (!isStatsigReady()) {
    console.warn('Statsig is not ready. Waiting for initialization...')
    // Wait for initialization if it's in progress
    if (initializationPromise) {
      try {
        await initializationPromise
      } catch (error) {
        console.warn('Statsig initialization failed, proceeding anyway')
      }
    }
    if (!isStatsigReady()) {
      console.error('Statsig initialization failed or timed out')
      return
    }
  }

  try {
    await Statsig.updateUserAsync(user, { timeoutMs: 3000 })
  } catch (error) {
    console.error('Statsig updateUser error:', error)
  }
}
